import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Spin, Form, message } from 'antd';
import commonS from 'styles/common.module.less';
import s from './s.module.less';

import WeekItem from './components/WeekItem';
import TimeZoneSelect from 'components/form/TimeZoneSelect';

import { TimeFormFieldEnum, TimeFormInterface } from './types';
import { providerAvailabilitySave } from 'api/appointmentSetting';
import { IproviderAvailabilityItem } from 'types/appointmentSetting';
import { getUserId, setTimeZone } from 'utils/localstore';
import useAvailability, { initData } from 'hooks/useAvailability';
import useTimezone from 'hooks/useTimezone';

interface TimezoneOperationhoursProps {
    autoSave?: boolean;
    scheduleToken?: string;
    providerId?: number;
}

const TimezoneOperationhours = forwardRef((props: TimezoneOperationhoursProps, ref) => {
    const { autoSave = true, scheduleToken, providerId } = props;
    const [form] = Form.useForm<TimeFormInterface>();
    const [weekList, setWeekList] = useState<IproviderAvailabilityItem[]>([]);
    const [availabilityList, loading, refetch] = useAvailability(providerId, scheduleToken);
    const [allTimezone, myTimezone, , , refetchTimezone] = useTimezone();
    const uid = providerId || getUserId();

    const onUpdateData = async (newWeekList?: IproviderAvailabilityItem[], forceSave = false) => {
        if (!autoSave && !forceSave) {
            return;
        }
        try {
            const formValues = await form.validateFields();
            const params = {
                week: newWeekList ?? weekList,
                timezone: formValues[TimeFormFieldEnum.TIME_ZONE],
                providerId: uid,
            };
            const { data } = await providerAvailabilitySave(params, uid!, scheduleToken);
            if (data.code === 200) {
                setTimeZone(formValues[TimeFormFieldEnum.TIME_ZONE]);
                refetchTimezone();
            } else {
                message.error(data.message);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getMissingDays = useCallback((): string[] => {
        const missingDays: string[] = [];
        weekList.forEach((item) => {
            if (!item.hourFromTo || item.hourFromTo.length === 0) {
                if (item.weekDay) {
                    missingDays.push(item.weekDay);
                }
            } else {
                const hasValidTime = item.hourFromTo.some((time) => {
                    return (
                        time.fromHour !== undefined &&
                        time.fromMinute !== undefined &&
                        time.toHour !== undefined &&
                        time.toMinute !== undefined
                    );
                });
                if (!hasValidTime && item.weekDay) {
                    missingDays.push(item.weekDay);
                }
            }
        });
        return missingDays;
    }, [weekList]);

    useImperativeHandle(ref, () => ({
        onUpdate: () => {
            return onUpdateData(weekList, true);
        },
        getMissingDays: () => {
            return getMissingDays();
        },
    }));

    const changeTimeList = useCallback(
        (val: IproviderAvailabilityItem) => {
            setWeekList((preWeekList) => {
                const newWeekList = preWeekList.map((item) =>
                    item.weekDay === val.weekDay ? { ...item, hourFromTo: val.hourFromTo } : item,
                );
                onUpdateData(newWeekList);
                return newWeekList;
            });
        },
        [onUpdateData],
    );

    const sortByWeekDay = (a: IproviderAvailabilityItem, b: IproviderAvailabilityItem) => {
        const day: any = {
            sunday: 0,
            monday: 1,
            tuesday: 2,
            wednesday: 3,
            thursday: 4,
            friday: 5,
            saturday: 6,
        };
        return day[a.weekDay!] - day[b.weekDay!];
    };

    useEffect(() => {
        if (availabilityList) {
            const { timezone, week, isInit } = availabilityList;
            const currentTimezone = myTimezone.value as string;
            let defaultTimezone = timezone || currentTimezone || initData.timezone;
            if (isInit) {
                defaultTimezone = currentTimezone || initData.timezone;
            }
            form.setFieldsValue({ [TimeFormFieldEnum.TIME_ZONE]: defaultTimezone });
            if (week && week?.length > 0) {
                week.sort(sortByWeekDay);
                setWeekList(week);
            } else {
                setWeekList(initData.week!);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availabilityList]);

    return (
        <Spin spinning={loading}>
            <div className={s.wrap}>
                <Form
                    form={form}
                    name="basic"
                    className={commonS.formStyle1}
                    initialValues={{ remember: true }}
                    autoComplete="off"
                    layout="vertical"
                >
                    <Form.Item
                        label={<div className={s.hourTitle}>Time zone</div>}
                        name={[TimeFormFieldEnum.TIME_ZONE]}
                        validateFirst
                        rules={[{ required: true, message: 'Time zone is required' }]}
                    >
                        <TimeZoneSelect className={s.timezone} onChange={() => onUpdateData()} />
                    </Form.Item>
                </Form>

                <div className={s.weekList}>
                    {weekList?.map((item, index) => (
                        <WeekItem key={item.weekDay || index} weekItemData={item} changeTimeList={changeTimeList} />
                    ))}
                </div>
            </div>
        </Spin>
    );
});

TimezoneOperationhours.displayName = 'TimezoneOperationhours';

export default TimezoneOperationhours;
